import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import HighlightMark from '../../MyComponents/LandingSections/HighlightMark';
import AboutHome from './AboutHome';
import AboutMedia from './AboutMedia';
import AboutPartners from './AboutPartners';
import AboutTeam from './AboutTeam';
import {
    ABOUT_US_HOME_LINK,
    ABOUT_US_MEDIA_LINK,
    ABOUT_US_PARTNERS_LINK,
    ABOUT_US_TEAM_LINK,
} from '../../common/urlLinks';
import { cx } from '../Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const TABS = [
    { key: 'home', to: ABOUT_US_HOME_LINK, labelId: 'aboutUs.ourMission.button', Component: AboutHome },
    { key: 'team', to: ABOUT_US_TEAM_LINK, labelId: 'aboutUs.team.button', Component: AboutTeam },
    { key: 'partners', to: ABOUT_US_PARTNERS_LINK, labelId: 'aboutUs.partners.button', Component: AboutPartners },
    { key: 'media', to: ABOUT_US_MEDIA_LINK, labelId: 'aboutUs.media.button', Component: AboutMedia },
];

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '820px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 18px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '680px',
        margin: '0 auto',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    // The sub-page switcher, on the pricing section's segmented toggle.
    tabs: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '30px',
    },
    track: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '2px',
        padding: '4px',
        borderRadius: '999px',
        background: REDESIGN_COLORS.toggleTrack,
    },
    tab: {
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: '40px',
        padding: '0 18px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        textDecoration: 'none',
        '&:hover': {
            color: REDESIGN_COLORS.link,
        },
    },
    tabOn: {
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        boxShadow: '0 1px 3px rgba(6, 63, 69, 0.12)',
        '&:hover': {
            color: REDESIGN_COLORS.dark,
        },
    },
    version: {
        margin: '8px 0 0',
        color: REDESIGN_COLORS.muted,
        fontSize: '0.72rem',
        fontWeight: 600,
        textAlign: 'right',
    },
}));

/**
 * The about page: one hero with the four sub-pages (mission, team, partners,
 * media) behind a pill switcher that keeps the existing /about/<key> routes.
 */
const About = () => {
    const classes = useStyles();
    const { aboutValue } = useParams();
    const active = TABS.find((tab) => tab.key === aboutValue) || TABS[0];
    const Page = active.Component;

    return (
        <>
            <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={24}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="redesign.about.eyebrow" />
                        </SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>
                        <FormattedMessage
                            id="redesign.about.title"
                            values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                        />
                    </h1>
                    <p className={classes.lead}>
                        <FormattedMessage id="redesign.about.lead" />
                    </p>
                </div>
                <nav className={classes.tabs} aria-label="about">
                    <div className={classes.track}>
                        {TABS.map((tab) => (
                            <Link
                                key={tab.key}
                                to={tab.to}
                                aria-current={tab === active ? 'page' : undefined}
                                className={cx(classes.tab, tab === active && classes.tabOn)}
                            >
                                <FormattedMessage id={tab.labelId} />
                            </Link>
                        ))}
                    </div>
                </nav>
            </LandingSection>

            <LandingSection maxWidth={1080} paddingTop={16} paddingBottom={56}>
                <Page />
                <p className={classes.version}>
                    <FormattedMessage id="redesign.about.version" values={{ version: window.BUILD_VERSION }} />
                </p>
            </LandingSection>
        </>
    );
};

export default About;
